import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Icons } from './Icons';
import ConsentSettingsModal from './ConsentSettingsModal';

export default function LoginPage() {
  const {
    loginWithGoogle, loginWithFacebook, loginWithGithub,
    loginWithEmail, registerWithEmail, resetPassword, error, clearError,
  } = useAuth();

  const [mode, setMode]             = useState('login');     // 'login' | 'register' | 'reset'
  const [email, setEmail]           = useState('');
  const [password, setPassword]     = useState('');
  const [displayName, setDisplayName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [resetSent, setResetSent]   = useState(false);
  const [showCookieSettings, setShowCookieSettings] = useState(false);

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    if (mode === 'register') {
      await registerWithEmail(email, password, displayName);
    } else {
      await loginWithEmail(email, password);
    }
    setSubmitting(false);
  };

  const switchMode = () => {
    clearError();
    setResetSent(false);
    setMode(mode === 'login' ? 'register' : 'login');
  };

  const handleResetSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const ok = await resetPassword(email);
    if (ok) setResetSent(true);
    setSubmitting(false);
  };

  return (
    <div style={styles.wrapper}>
      {/* Decorative background */}
      <div style={styles.bgDots} />
      <div style={styles.bgGradient} />

      <div style={styles.card}>
        {/* Logo */}
        <div style={styles.logoRow}>
          <img src="/kt_logo.png" alt="Kinship Trees" style={styles.logoImg} />
          <div>
            <h1 style={styles.logoTitle}>Kinship Trees</h1>
            <p style={styles.logoSub}>Family History</p>
          </div>
        </div>

        <h2 style={styles.heading}>
          {mode === 'login' ? 'Welcome back' : mode === 'register' ? 'Create your account' : 'Reset your password'}
        </h2>
        <p style={styles.subheading}>
          {mode === 'login'
            ? 'Sign in to continue building your family tree'
            : mode === 'register'
            ? 'Start preserving your family history today'
            : "Enter your email and we'll send you a reset link"}
        </p>

        {/* Reset password view */}
        {mode === 'reset' && (
          <>
            {resetSent ? (
              <div style={{ ...styles.errorBox, background: '#EBF7EC', border: '1px solid #A8D5AA', color: '#2E7D32', marginBottom: 16 }}>
                Check your inbox — a reset link has been sent to <strong>{email}</strong>.
              </div>
            ) : (
              <form onSubmit={handleResetSubmit} style={styles.form}>
                <div style={styles.fieldGroup}>
                  <label style={styles.label}>Email</label>
                  <div style={styles.inputWrap}>
                    <span style={styles.inputIcon}><Icons.Mail size={15} /></span>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      style={{ ...styles.input, paddingLeft: 38 }}
                      required
                      autoFocus
                    />
                  </div>
                </div>
                {error && <div style={styles.errorBox}>{error}</div>}
                <button type="submit" disabled={submitting} style={styles.submitBtn}>
                  {submitting ? <span style={styles.spinner} /> : 'Send Reset Link'}
                </button>
              </form>
            )}
            <p style={styles.toggleText}>
              <button onClick={() => { clearError(); setResetSent(false); setMode('login'); }} style={styles.toggleBtn}>
                ← Back to sign in
              </button>
            </p>
          </>
        )}

        {/* Social logins + email form — only for login/register */}
        {mode !== 'reset' && (
          <>
            <div style={styles.socialRow}>
              <button onClick={loginWithGoogle} style={styles.socialBtn}>
                <Icons.Google /> Google
              </button>
              <button onClick={loginWithFacebook} style={{ ...styles.socialBtn, color: '#1877F2' }}>
                <Icons.Facebook /> Facebook
              </button>
              <button onClick={loginWithGithub} style={styles.socialBtn}>
                <Icons.Github /> GitHub
              </button>
            </div>

            {/* Divider */}
            <div style={styles.divider}>
              <div style={styles.dividerLine} />
              <span style={styles.dividerText}>or continue with email</span>
              <div style={styles.dividerLine} />
            </div>

            {/* Email form */}
            <form onSubmit={handleEmailSubmit} style={styles.form}>
              {mode === 'register' && (
                <div style={styles.fieldGroup}>
                  <label style={styles.label}>Full Name</label>
                  <input
                    type="text"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    placeholder="e.g. Jane Mitchell"
                    style={styles.input}
                    required
                  />
                </div>
              )}
              <div style={styles.fieldGroup}>
                <label style={styles.label}>Email</label>
                <div style={styles.inputWrap}>
                  <span style={styles.inputIcon}><Icons.Mail size={15} /></span>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    style={{ ...styles.input, paddingLeft: 38 }}
                    required
                  />
                </div>
              </div>
              <div style={styles.fieldGroup}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                  <label style={styles.label}>Password</label>
                  {mode === 'login' && (
                    <button
                      type="button"
                      onClick={() => { clearError(); setMode('reset'); }}
                      style={{ ...styles.toggleBtn, fontSize: 11 }}
                    >
                      Forgot password?
                    </button>
                  )}
                </div>
                <div style={styles.inputWrap}>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={mode === 'register' ? 'At least 6 characters' : '••••••••'}
                    style={{ ...styles.input, paddingRight: 40 }}
                    required
                    minLength={6}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    style={styles.eyeBtn}
                  >
                    {showPassword ? <Icons.EyeOff size={15} /> : <Icons.Eye size={15} />}
                  </button>
                </div>
              </div>

              {error && <div style={styles.errorBox}>{error}</div>}

              <button type="submit" disabled={submitting} style={styles.submitBtn}>
                {submitting ? (
                  <span style={styles.spinner} />
                ) : mode === 'login' ? 'Sign In' : 'Create Account'}
              </button>
            </form>

            {/* Toggle mode */}
            <p style={styles.toggleText}>
              {mode === 'login' ? "Don't have an account?" : 'Already have an account?'}{' '}
              <button onClick={switchMode} style={styles.toggleBtn}>
                {mode === 'login' ? 'Create one' : 'Sign in'}
              </button>
            </p>
          </>
        )}
      </div>

      {/* Footer.
          The cookie settings link is here because the privacy policy promises
          one on every page, and for a visitor arriving from an advert this is
          the first page of ours they see while signed out. */}
      <p style={styles.footer}>
        Built with care for preserving family stories
        <br />
        Need help? <a href="mailto:support@kinshiptrees.com" style={styles.footerLink}>support@kinshiptrees.com</a>
        <br />
        <button
          type="button"
          onClick={() => setShowCookieSettings(true)}
          style={styles.footerButton}
        >
          Cookie settings
        </button>
      </p>

      {showCookieSettings && <ConsentSettingsModal onClose={() => setShowCookieSettings(false)} />}
    </div>
  );
}

const styles = {
  wrapper: {
    minHeight: '100vh', display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center',
    background: '#FAF7F2', position: 'relative', overflow: 'hidden',
    fontFamily: "'DM Sans', sans-serif",
  },
  bgDots: {
    position: 'absolute', inset: 0, opacity: 0.25,
    backgroundImage: 'radial-gradient(circle, #C4975A 0.5px, transparent 0.5px)',
    backgroundSize: '30px 30px', pointerEvents: 'none',
  },
  bgGradient: {
    position: 'absolute', inset: 0, pointerEvents: 'none',
    background: 'radial-gradient(ellipse at 50% 0%, rgba(196,151,90,0.12) 0%, transparent 60%)',
  },
  card: {
    position: 'relative', zIndex: 1,
    width: 440, maxWidth: '92vw', padding: '40px 36px',
    background: '#FDFBF7', borderRadius: 20,
    boxShadow: '0 8px 40px rgba(44,36,24,0.1), 0 1px 3px rgba(44,36,24,0.05)',
    border: '1px solid #EBE4D8',
  },
  logoRow: {
    display: 'flex', alignItems: 'center', gap: 12, marginBottom: 28,
  },
  logoImg: {
    width: 52, height: 52, objectFit: 'contain', flexShrink: 0,
  },
  logoTitle: {
    margin: 0, fontSize: 22, fontFamily: "'Crimson Pro', Georgia, serif",
    fontWeight: 700, color: '#2C2418', lineHeight: 1.1,
  },
  logoSub: {
    margin: 0, fontSize: 10, color: '#A0937F', letterSpacing: 1.8,
    textTransform: 'uppercase', fontWeight: 600,
  },
  heading: {
    margin: '0 0 6px', fontSize: 24, fontFamily: "'Crimson Pro', Georgia, serif",
    fontWeight: 700, color: '#2C2418',
  },
  subheading: {
    margin: '0 0 24px', fontSize: 14, color: '#8B7D6B', lineHeight: 1.5,
  },
  socialRow: {
    display: 'flex', gap: 10, marginBottom: 20,
  },
  socialBtn: {
    flex: 1, padding: '10px 0', borderRadius: 10,
    border: '1.5px solid #E8E0D4', background: '#fff',
    cursor: 'pointer', fontSize: 12.5, fontWeight: 600,
    fontFamily: "'DM Sans', sans-serif", color: '#2C2418',
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7,
    transition: 'border-color 0.15s, box-shadow 0.15s',
  },
  divider: {
    display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20,
  },
  dividerLine: { flex: 1, height: 1, background: '#E8E0D4' },
  dividerText: { fontSize: 11, color: '#A0937F', whiteSpace: 'nowrap', fontWeight: 500 },
  form: {
    display: 'flex', flexDirection: 'column', gap: 16,
  },
  fieldGroup: {
    display: 'flex', flexDirection: 'column', gap: 5,
  },
  label: {
    fontSize: 12, fontWeight: 600, color: '#6B5E4F',
    textTransform: 'uppercase', letterSpacing: 0.8,
  },
  inputWrap: { position: 'relative' },
  inputIcon: {
    position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)',
    color: '#B8AFA0', pointerEvents: 'none',
  },
  input: {
    width: '100%', padding: '11px 14px', borderRadius: 10,
    border: '1.5px solid #D5CAB8', background: '#fff',
    fontSize: 14, color: '#2C2418', outline: 'none',
    fontFamily: "'DM Sans', sans-serif", boxSizing: 'border-box',
    transition: 'border-color 0.15s',
  },
  eyeBtn: {
    position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
    background: 'none', border: 'none', cursor: 'pointer', color: '#A0937F',
    padding: 4,
  },
  errorBox: {
    padding: '10px 14px', borderRadius: 10,
    background: '#FDE8E8', border: '1px solid #F5C6C6',
    color: '#C0392B', fontSize: 13, fontWeight: 500,
  },
  submitBtn: {
    padding: '12px 0', borderRadius: 10, border: 'none',
    background: '#3B2F1E', color: '#F5EDE3',
    fontSize: 14, fontWeight: 700, cursor: 'pointer',
    fontFamily: "'DM Sans', sans-serif",
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    marginTop: 4, transition: 'background 0.15s',
    minHeight: 46,
  },
  spinner: {
    display: 'inline-block', width: 18, height: 18,
    border: '2px solid rgba(245,237,227,0.3)',
    borderTop: '2px solid #F5EDE3', borderRadius: '50%',
    animation: 'spin 0.7s linear infinite',
  },
  toggleText: {
    textAlign: 'center', fontSize: 13, color: '#8B7D6B',
    marginTop: 20, marginBottom: 0,
  },
  toggleBtn: {
    background: 'none', border: 'none', color: '#C4975A',
    fontWeight: 700, cursor: 'pointer', fontSize: 13,
    fontFamily: "'DM Sans', sans-serif", textDecoration: 'underline',
    textUnderlineOffset: 2,
  },
  footer: {
    position: 'relative', zIndex: 1, marginTop: 24,
    fontSize: 12, color: '#B8AFA0', textAlign: 'center', lineHeight: 1.8,
  },
  footerLink: {
    color: '#B8AFA0', textDecoration: 'underline', textUnderlineOffset: 2,
  },
  footerButton: {
    background: 'none', border: 'none', padding: 0, cursor: 'pointer',
    font: 'inherit', color: '#B8AFA0', textDecoration: 'underline',
    textUnderlineOffset: 2,
  },
};
